// Tenant-scoped TanStack Query wrappers for the Customer CRM. Keeps cache
// keys consistent across pages and centralises the retry/stale policy.

import { useMutation, useQuery, useQueryClient, keepPreviousData, type UseQueryResult } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import {
  customersApi,
  type CustomerListParams,
  type FeedbackListParams,
  type FeedbackSummaryParams,
} from './api';
import type {
  Customer,
  CustomerConsentInput,
  CustomerCreateInput,
  CustomerFeedbackList,
  CustomerFeedbackSummary,
  CustomerListResult,
  CustomerPurchaseRow,
  CommunicationLogRow,
  CustomerVoucherRedemptionRow,
  LoyaltySummary,
  RfmAnalysisData,
  StoreCreditLedgerRow,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useCustomerList(params: CustomerListParams): UseQueryResult<CustomerListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'customers', tenantId, 'list', params.page ?? 1, params.search ?? '', params.segment ?? '', params.sort ?? '',
      params.name ?? '', params.mobile ?? '', params.email ?? '', params.start_date ?? '', params.end_date ?? '',
      params.active ?? '', params.period ?? '',
    ],
    queryFn: () => customersApi.list(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCustomer(id: string | undefined): UseQueryResult<Customer | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['customers', tenantId, 'detail', id],
    queryFn: () => (id ? customersApi.get(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCustomerLoyalty(id: string | undefined): UseQueryResult<LoyaltySummary> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['customers', tenantId, 'loyalty', id],
    queryFn: () => customersApi.loyalty(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCustomerPurchases(
  id: string | undefined,
  page: number,
): UseQueryResult<{ data: CustomerPurchaseRow[]; total: number }> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['customers', tenantId, 'purchases', id, page],
    queryFn: () => customersApi.purchases(id!, page),
    enabled: Boolean(id),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCustomerStoreCredit(id: string | undefined): UseQueryResult<StoreCreditLedgerRow[]> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['customers', tenantId, 'store-credit', id],
    queryFn: () => customersApi.storeCreditLedger(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCustomerVoucherRedemptions(
  id: string | undefined,
): UseQueryResult<{ data: CustomerVoucherRedemptionRow[]; total: number }> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['customers', tenantId, 'voucher-redemptions', id],
    queryFn: () => customersApi.voucherRedemptions(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCustomerCommunications(id: string | undefined): UseQueryResult<CommunicationLogRow[]> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['customers', tenantId, 'communications', id],
    queryFn: () => customersApi.communications(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useRfmAnalysis(period: RfmAnalysisData['period']): UseQueryResult<RfmAnalysisData> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['customers', tenantId, 'rfm', period],
    queryFn: () => customersApi.rfmAnalysis(period),
    staleTime: 60_000,
  });
}

export function useFeedbackSummary(
  params: FeedbackSummaryParams = {},
): UseQueryResult<CustomerFeedbackSummary> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'customers',
      tenantId,
      'feedback',
      'summary',
      params.rating ?? 0,
      params.location_id ?? '',
      params.from ?? '',
      params.to ?? '',
    ],
    queryFn: () => customersApi.feedbackSummary(params),
    staleTime: 60_000,
  });
}

export function useFeedbackList(params: FeedbackListParams): UseQueryResult<CustomerFeedbackList> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'customers',
      tenantId,
      'feedback',
      'list',
      params.page ?? 1,
      params.rating ?? 0,
      params.location_id ?? '',
      params.from ?? '',
      params.to ?? '',
    ],
    queryFn: () => customersApi.feedbackList(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useCreateCustomer() {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (input: CustomerCreateInput) => customersApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', tenantId, 'list'] });
    },
  });
}

export function useUpdateCustomer(id: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (input: Parameters<typeof customersApi.update>[1]) => customersApi.update(id, input),
    onSuccess: () => {
      // The backend PUT response is a partial stub ({id, name, email, tags,
      // updated_at}) — writing it to the detail cache would clobber required
      // fields like visit_count / loyalty_points / total_spent that the
      // Customer 360 KPI tiles call .toLocaleString() / formatPaise() on,
      // crashing the page. Invalidate instead so the next render keeps the
      // existing full Customer until the refetch completes. See issue #117.
      qc.invalidateQueries({ queryKey: ['customers', tenantId, 'detail', id] });
      qc.invalidateQueries({ queryKey: ['customers', tenantId, 'list'] });
    },
  });
}

/** Deactivate (active=false) or reactivate (active=true) a customer — CU1-004. */
export function useSetCustomerActive(id: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (active: boolean) => (active ? customersApi.reactivate(id) : customersApi.deactivate(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', tenantId, 'detail', id] });
      qc.invalidateQueries({ queryKey: ['customers', tenantId, 'list'] });
    },
  });
}

/** Set a customer's marketing opt-outs (CU4-003). */
export function useSetCustomerConsent(id: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (input: CustomerConsentInput) => customersApi.setConsent(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', tenantId, 'detail', id] });
    },
  });
}

/** Preview a merge of `id` into `into` (CU1-005); idle until a target is picked. */
export function useCustomerMergePreview(id: string, into: string | undefined) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['customers', tenantId, 'merge-preview', id, into ?? ''],
    queryFn: () => customersApi.mergePreview(id, into as string),
    enabled: Boolean(into),
    staleTime: 0,
  });
}

/** Merge `id` into a target customer — irreversible (CU1-005). */
export function useMergeCustomer(id: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (into: string) => customersApi.mergeInto(id, into),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', tenantId] });
    },
  });
}

export function useAdjustLoyaltyPoints(id: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: ({ amount, reason }: { amount: number; reason: string }) =>
      customersApi.adjustLoyaltyPoints(id, amount, reason),
    onSuccess: (summary) => {
      qc.setQueryData(['customers', tenantId, 'loyalty', id], summary);
      qc.invalidateQueries({ queryKey: ['customers', tenantId, 'detail', id] });
    },
  });
}
